import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import type { CartLine } from './useCatalogCart';

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

interface CheckoutDialogProps {
  open: boolean;
  lines: CartLine[];
  total: number;
  checkingOut: boolean;
  error: string | null;
  initialPhone: string;
  onClose: () => void;
  onConfirm: (phone: string) => void;
}

export default function CheckoutDialog({
  open,
  lines,
  total,
  checkingOut,
  error,
  initialPhone,
  onClose,
  onConfirm,
}: CheckoutDialogProps) {
  const [phone, setPhone] = useState(initialPhone);

  // Prefill with the phone remembered on this device each time the dialog opens; the client can still edit it
  useEffect(() => {
    if (open) {
      setPhone(initialPhone);
    }
  }, [open, initialPhone]);

  const trimmedPhone = phone.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (trimmedPhone.length === 0 || checkingOut) {
      return;
    }
    onConfirm(trimmedPhone);
  };

  return (
    <Dialog
      open={open}
      onClose={checkingOut ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Оформлення замовлення</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {/* Order summary */}
          <List
            dense
            sx={{
              maxHeight: 260,
              overflowY: 'auto',
              mb: 1,
            }}
          >
            {lines.map((line) => (
              <ListItem
                key={line.productId}
                divider
                disableGutters
              >
                <ListItemText
                  primary={line.productName}
                  secondary={`${line.quantity} × ${formatPrice(line.unitPrice)}`}
                />
                <Typography variant="body2">
                  {formatPrice(line.quantity * line.unitPrice)}
                </Typography>
              </ListItem>
            ))}
          </List>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'right',
              mb: 2,
            }}
          >
            Разом: {formatPrice(total)}
          </Typography>
          <TextField
            label="Номер телефону"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
            required
            autoFocus
            fullWidth
            helperText="Для зв'язку щодо замовлення"
            slotProps={{ htmlInput: { maxLength: 32 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={onClose}
            disabled={checkingOut}
          >
            Скасувати
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={trimmedPhone.length === 0 || checkingOut}
          >
            {checkingOut ? 'Оформлення…' : 'Підтвердити'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
